import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  PRO_LIMITS,
  FREE_LIMITS,
  createProContent,
  readVideoDuration,
  formatDuration,
} from "../../services/proApi";
// The Free Write Blog photo editor (crop, filters, adjust), reused as-is.
import {
  CropEditor,
  FILTERS,
  ADJUST_SLIDERS,
  DEFAULT_ADJUST,
  buildImageFilter,
  bakeEditedImage,
} from "../AddBlog";
import "../AddBlog.css";
import "./Pro.css";

// Same "has this photo been edited?" test the free composer applies before
// baking: any filter other than Normal, or any slider off its default.
const hasImageEdits = (img) =>
  img.filter !== 0 ||
  Object.keys(DEFAULT_ADJUST).some((key) => img.adjust[key] !== DEFAULT_ADJUST[key]);

// Every video-limit message is worded from PRO_LIMITS, so none can disagree.
const VIDEO_LIMIT_HOURS = PRO_LIMITS.VIDEO_SECONDS_MAX / 3600;
const VIDEO_LIMIT_TEXT = `${VIDEO_LIMIT_HOURS} hour${VIDEO_LIMIT_HOURS === 1 ? "" : "s"}`;
const VIDEO_TOO_LONG = `Video must be ${VIDEO_LIMIT_TEXT} or shorter`;

const vignetteShadow = (vignette) =>
  vignette > 0
    ? `inset 0 0 ${vignette * 2.5}px ${vignette * 0.8}px rgba(0,0,0,0.75)`
    : "none";

// =====================================================
// PRO BLOG CREATION PAGE  (Task 6)
//
// The Pro composer. Same publish action as the free one ("Create Blog/Post"),
// with the Pro allowances:
//
//     Title    250 characters       (free: 100)
//     Content  2,000 characters     (free: 500)
//     Images   10                   (free: 1)
//     Video    1 hour               (free: 1 minute)
//
// Every limit is enforced here BEFORE the request leaves the browser, and
// again — independently — by the server. This layer exists so a user is told
// early and clearly; it is not what makes the limits hold.
//
// Multiple images are shown as a GALLERY: every selected image keeps its own
// tile and its own remove button, so picking a second image never replaces the
// preview of the first.
//
// PHOTO EDITING is the free composer's editor (crop, filters, adjust), applied
// per image: tap a tile to edit it. Edits are baked into the file on publish
// exactly as the free composer does, and the cover image's crop ratio becomes
// the post's aspectRatio.
// =====================================================

function ProCreateContent() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // { id, file, url, filter, adjust, ratioLabel } — filter/adjust/ratioLabel
  // are the free editor's per-photo edit state.
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null); // { file, url, duration }

  const [editingId, setEditingId] = useState(null);
  const [editTab, setEditTab] = useState("filters");
  const [showCrop, setShowCrop] = useState(false);

  const [hideComments, setHideComments] = useState(false);
  const [allowShare, setAllowShare] = useState(true);
  const [allowSave, setAllowSave] = useState(true);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const imageIdRef = useRef(0);

  // Object URLs are created per selected file. The unmount cleanup has to see
  // the LATEST selection, not the one captured when the effect first ran, so
  // the current media is mirrored into a ref the teardown reads.
  const mediaRef = useRef({ images: [], video: null });
  mediaRef.current = { images, video };

  useEffect(() => {
    return () => {
      mediaRef.current.images.forEach((img) => URL.revokeObjectURL(img.url));
      if (mediaRef.current.video) URL.revokeObjectURL(mediaRef.current.video.url);
    };
  }, []);

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const setError = (field, message) =>
    setErrors((prev) => ({ ...prev, [field]: message }));

  const clearError = (field) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

  // ── TITLE / CONTENT ──
  // The inputs are capped with maxLength so the limit cannot be typed past,
  // and a pasted over-length value is trimmed to the limit with a message
  // rather than silently accepted.
  const onTitleChange = (e) => {
    const value = e.target.value;

    if (value.length > PRO_LIMITS.TITLE_MAX) {
      setTitle(value.slice(0, PRO_LIMITS.TITLE_MAX));
      setError("title", `Title cannot exceed ${PRO_LIMITS.TITLE_MAX} characters`);
      return;
    }

    clearError("title");
    setTitle(value);
  };

  const onContentChange = (e) => {
    const value = e.target.value;

    if (value.length > PRO_LIMITS.CONTENT_MAX) {
      setContent(value.slice(0, PRO_LIMITS.CONTENT_MAX));
      setError("content", `Content cannot exceed ${PRO_LIMITS.CONTENT_MAX} characters`);
      return;
    }

    clearError("content");
    setContent(value);
  };

  // ── IMAGES (up to PRO_LIMITS.IMAGES_MAX, gallery) ──
  const onImagesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    const room = PRO_LIMITS.IMAGES_MAX - images.length;

    if (room <= 0) {
      setError("images", `You can upload a maximum of ${PRO_LIMITS.IMAGES_MAX} images`);
      showToast(`Maximum ${PRO_LIMITS.IMAGES_MAX} images ❌`, "error");
      e.target.value = "";
      return;
    }

    const accepted = picked.slice(0, room);
    const rejected = picked.length - accepted.length;

    const added = accepted.map((file) => {
      imageIdRef.current += 1;
      return {
        id: imageIdRef.current,
        file,
        url: URL.createObjectURL(file),
        filter: 0,
        adjust: { ...DEFAULT_ADJUST },
        ratioLabel: null,
      };
    });

    setImages((prev) => [...prev, ...added]);

    // Like the free composer, a freshly picked photo opens in the editor.
    if (added.length && editingId === null) setEditingId(added[0].id);

    if (rejected > 0) {
      setError(
        "images",
        `Only ${PRO_LIMITS.IMAGES_MAX} images are allowed — ${rejected} file${
          rejected === 1 ? " was" : "s were"
        } not added`
      );
      showToast(`Maximum ${PRO_LIMITS.IMAGES_MAX} images ❌`, "error");
    } else {
      clearError("images");
    }

    e.target.value = "";
  };

  const removeImage = (id) => {
    const target = images.find((img) => img.id === id);
    if (target) URL.revokeObjectURL(target.url);

    const remaining = images.filter((img) => img.id !== id);
    setImages(remaining);

    if (editingId === id) setEditingId(remaining.length ? remaining[0].id : null);
    clearError("images");
  };

  const clearImages = () => {
    images.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
    setEditingId(null);
    setShowCrop(false);
    clearError("images");
  };

  // ── PHOTO EDITING (the free composer's editor, per image) ──
  const editingImage = images.find((img) => img.id === editingId) || null;

  const updateImage = (id, patch) =>
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, ...patch } : img)));

  // The crop result replaces the photo, exactly as in the free composer.
  const onCropDone = (blob, url, ratioLabel) => {
    setShowCrop(false);
    if (!editingImage) return;

    URL.revokeObjectURL(editingImage.url);
    updateImage(editingImage.id, {
      file: new File([blob], editingImage.file?.name || "cropped.jpg", { type: "image/jpeg" }),
      url,
      ratioLabel: ratioLabel && ratioLabel !== "Free" ? ratioLabel : editingImage.ratioLabel,
    });
  };

  // ── VIDEO (one, up to PRO_LIMITS.VIDEO_SECONDS_MAX) ──
  const onVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    let duration = 0;

    try {
      duration = await readVideoDuration(file);
    } catch (err) {
      setError("video", "Could not read this video file");
      showToast("Could not read this video ❌", "error");
      return;
    }

    if (duration > PRO_LIMITS.VIDEO_SECONDS_MAX) {
      setError("video", `${VIDEO_TOO_LONG} — this one is ${formatDuration(duration)}`);
      showToast(`${VIDEO_TOO_LONG} ❌`, "error");
      return;
    }

    if (video) URL.revokeObjectURL(video.url);

    clearError("video");
    setVideo({ file, url: URL.createObjectURL(file), duration });
  };

  const removeVideo = () => {
    if (video) URL.revokeObjectURL(video.url);
    setVideo(null);
    clearError("video");
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const resetForm = () => {
    setTitle("");
    setContent("");
    clearImages();
    removeVideo();
    setHideComments(false);
    setAllowShare(true);
    setAllowSave(true);
    setProgress(0);
    setErrors({});
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  // ── PUBLISH ──
  const publish = async (e) => {
    e.preventDefault();

    // Front-end gate: nothing over a limit is allowed to be submitted.
    if (title.length > PRO_LIMITS.TITLE_MAX) {
      setError("title", `Title cannot exceed ${PRO_LIMITS.TITLE_MAX} characters`);
      return showToast(`Title max ${PRO_LIMITS.TITLE_MAX} characters`, "error");
    }

    if (content.length > PRO_LIMITS.CONTENT_MAX) {
      setError("content", `Content cannot exceed ${PRO_LIMITS.CONTENT_MAX} characters`);
      return showToast(`Content max ${PRO_LIMITS.CONTENT_MAX} characters`, "error");
    }

    if (images.length > PRO_LIMITS.IMAGES_MAX) {
      setError("images", `You can upload a maximum of ${PRO_LIMITS.IMAGES_MAX} images`);
      return showToast(`Maximum ${PRO_LIMITS.IMAGES_MAX} images`, "error");
    }

    if (video && video.duration > PRO_LIMITS.VIDEO_SECONDS_MAX) {
      setError("video", VIDEO_TOO_LONG);
      return showToast(VIDEO_TOO_LONG, "error");
    }

    if (!title && !content && !images.length && !video) {
      return showToast("Add a title, content, an image or a video", "error");
    }

    try {
      setLoading(true);
      setProgress(0);

      const formData = new FormData();

      if (title) formData.append("title", title);
      if (content) formData.append("content", content);

      // Edited photos are baked (filter + adjust + vignette) into the file
      // that is uploaded, exactly as the free composer does; untouched photos
      // go up as picked. Order is preserved.
      for (const img of images) {
        if (hasImageEdits(img)) {
          const baked = await bakeEditedImage(
            img.url,
            buildImageFilter(img.filter, img.adjust),
            img.adjust.vignette
          );
          formData.append("images", new File([baked], img.file.name, { type: "image/jpeg" }));
        } else {
          formData.append("images", img.file);
        }
      }

      // The slider frames every photo at the post's ratio; like the free
      // composer, a crop ratio chosen for the cover photo sets it.
      if (images[0]?.ratioLabel) formData.append("aspectRatio", images[0].ratioLabel);

      if (video) {
        formData.append("video", video.file);
        formData.append("videoDuration", String(Math.round(video.duration)));
      }

      formData.append("hideComments", hideComments);
      formData.append("allowShare", allowShare);
      formData.append("allowSave", allowSave);

      await createProContent(formData, (p) => {
        if (p.total) setProgress(Math.round((p.loaded * 100) / p.total));
      });

      showToast("Pro Blog Published 🚀", "success");
      resetForm();

      // The new post is already in My Pro Profile — that page reads it from
      // the server, so no client-side bookkeeping is needed here.
      setTimeout(() => navigate("/pro-profile"), 900);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.field) setError(data.field, data.message);
      showToast(data?.message || "Publish failed ❌", "error");
    } finally {
      setLoading(false);
    }
  };

  const titlePct = (title.length / PRO_LIMITS.TITLE_MAX) * 100;
  const contentPct = (content.length / PRO_LIMITS.CONTENT_MAX) * 100;
  const imagePct = (images.length / PRO_LIMITS.IMAGES_MAX) * 100;

  const barColor = (pct) => (pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : "#a78bfa");

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {showCrop && editingImage && (
        <CropEditor
          src={editingImage.url}
          onDone={onCropDone}
          onCancel={() => setShowCrop(false)}
        />
      )}

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">✨ CREATE CONTENT</div>
          <h1 className="pro-hero-title">Pro Blog Creation</h1>
          <p className="pro-hero-sub">
            {PRO_LIMITS.TITLE_MAX}-character titles · {PRO_LIMITS.CONTENT_MAX}-character
            content · {PRO_LIMITS.IMAGES_MAX} images · {VIDEO_LIMIT_HOURS}-hour video
          </p>
        </header>

        <div className="pro-limits-table" role="table" aria-label="Content limits">
          <div className="pro-limits-row pro-limits-head" role="row">
            <span>Feature</span>
            <span>Free</span>
            <span>Pro</span>
          </div>
          <div className="pro-limits-row" role="row">
            <span>Title</span>
            <span>{FREE_LIMITS.TITLE_MAX}</span>
            <span className="pro-limits-pro">{PRO_LIMITS.TITLE_MAX}</span>
          </div>
          <div className="pro-limits-row" role="row">
            <span>Content</span>
            <span>{FREE_LIMITS.CONTENT_MAX}</span>
            <span className="pro-limits-pro">
              {PRO_LIMITS.CONTENT_MAX.toLocaleString()}
            </span>
          </div>
          <div className="pro-limits-row" role="row">
            <span>Images</span>
            <span>{FREE_LIMITS.IMAGES_MAX}</span>
            <span className="pro-limits-pro">{PRO_LIMITS.IMAGES_MAX}</span>
          </div>
          <div className="pro-limits-row" role="row">
            <span>Video</span>
            <span>1 minute</span>
            <span className="pro-limits-pro">{VIDEO_LIMIT_TEXT}</span>
          </div>
        </div>

        <form className="pro-card pro-form" onSubmit={publish}>
          {/* ── TITLE ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pro-title">
              📝 Title
            </label>
            <input
              id="pro-title"
              type="text"
              className={`pro-input${errors.title ? " pro-input-error" : ""}`}
              placeholder="Enter your blog title…"
              value={title}
              maxLength={PRO_LIMITS.TITLE_MAX}
              onChange={onTitleChange}
              disabled={loading}
            />
            <div className="pro-meter-row">
              <div className="pro-meter">
                <div
                  className="pro-meter-fill"
                  style={{ width: `${titlePct}%`, background: barColor(titlePct) }}
                />
              </div>
              <span className="pro-counter">
                {title.length}/{PRO_LIMITS.TITLE_MAX}
              </span>
            </div>
            {errors.title && <p className="pro-error">{errors.title}</p>}
          </div>

          {/* ── CONTENT ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pro-content">
              💬 Content
            </label>
            <textarea
              id="pro-content"
              className={`pro-input pro-textarea${
                errors.content ? " pro-input-error" : ""
              }`}
              placeholder="Write your blog content here…"
              value={content}
              maxLength={PRO_LIMITS.CONTENT_MAX}
              rows={10}
              onChange={onContentChange}
              disabled={loading}
            />
            <div className="pro-meter-row">
              <div className="pro-meter">
                <div
                  className="pro-meter-fill"
                  style={{ width: `${contentPct}%`, background: barColor(contentPct) }}
                />
              </div>
              <span className="pro-counter">
                {content.length}/{PRO_LIMITS.CONTENT_MAX}
              </span>
            </div>
            {errors.content && <p className="pro-error">{errors.content}</p>}
          </div>

          {/* ── IMAGES (GALLERY) ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pro-images">
              🖼 Images <span className="pro-optional">(up to {PRO_LIMITS.IMAGES_MAX})</span>
            </label>

            <div className="pro-file-row">
              <input
                ref={imageInputRef}
                id="pro-images"
                type="file"
                accept="image/*"
                multiple
                className="pro-file-input"
                onChange={onImagesChange}
                disabled={loading || images.length >= PRO_LIMITS.IMAGES_MAX}
              />
              <label htmlFor="pro-images" className="pro-file-label">
                {images.length >= PRO_LIMITS.IMAGES_MAX
                  ? `Limit reached (${PRO_LIMITS.IMAGES_MAX})`
                  : "Choose images…"}
              </label>

              {images.length > 0 && (
                <button
                  type="button"
                  className="pro-btn pro-btn-ghost"
                  onClick={clearImages}
                  disabled={loading}
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="pro-meter-row">
              <div className="pro-meter">
                <div
                  className="pro-meter-fill"
                  style={{ width: `${imagePct}%`, background: barColor(imagePct) }}
                />
              </div>
              <span className="pro-counter">
                {images.length}/{PRO_LIMITS.IMAGES_MAX}
              </span>
            </div>

            {errors.images && <p className="pro-error">{errors.images}</p>}

            {/* Every image keeps its own tile — selecting more never replaces
                the previous preview. */}
            {images.length > 0 && (
              <div className="pro-gallery" aria-label="Selected images">
                {images.map((img, index) => (
                  <figure
                    className={`pro-gallery-item${img.id === editingId ? " is-editing" : ""}`}
                    key={img.id}
                  >
                    <button
                      type="button"
                      className="pro-gallery-select"
                      onClick={() => setEditingId(img.id)}
                      disabled={loading}
                      aria-label={`Edit image ${index + 1}`}
                      aria-pressed={img.id === editingId}
                    >
                      <img
                        src={img.url}
                        alt={`Selected ${index + 1}`}
                        style={{ filter: buildImageFilter(img.filter, img.adjust) }}
                      />
                    </button>
                    <button
                      type="button"
                      className="pro-gallery-remove"
                      onClick={() => removeImage(img.id)}
                      disabled={loading}
                      aria-label={`Remove image ${index + 1}`}
                    >
                      ✕
                    </button>
                    <figcaption>
                      {index + 1}
                      {hasImageEdits(img) || img.ratioLabel ? " ✎" : ""}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            {/* The free composer's photo editor, for the selected tile. */}
            {editingImage && (
              <div className="media-editor pro-photo-editor">
                <div className="media-editor-preview-wrap">
                  <img
                    src={editingImage.url}
                    className="media-editor-preview"
                    style={{
                      filter: buildImageFilter(editingImage.filter, editingImage.adjust),
                      boxShadow: vignetteShadow(editingImage.adjust.vignette),
                    }}
                    alt={`Selected ${images.indexOf(editingImage) + 1}, being edited`}
                  />
                  <button
                    type="button"
                    className="media-editor-crop-btn"
                    onClick={() => setShowCrop(true)}
                    disabled={loading}
                  >
                    ✂ Crop
                  </button>
                </div>
                <div className="media-editor-tabs">
                  <button type="button" className={`med-tab${editTab === "filters" ? " active" : ""}`} onClick={() => setEditTab("filters")}>🎨 Filters</button>
                  <button type="button" className={`med-tab${editTab === "adjust" ? " active" : ""}`} onClick={() => setEditTab("adjust")}>🎛 Adjust</button>
                </div>
                {editTab === "filters" && (
                  <div className="med-filter-strip">
                    {FILTERS.map((f, i) => (
                      <button
                        type="button"
                        key={f.name}
                        className={`med-filter-btn${editingImage.filter === i ? " active" : ""}`}
                        onClick={() => updateImage(editingImage.id, { filter: i })}
                      >
                        <div className="med-filter-thumb" style={{ backgroundImage: `url(${editingImage.url})`, filter: f.css }} />
                        <span className="med-filter-name">{f.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {editTab === "adjust" && (
                  <div className="med-adjust-grid">
                    {ADJUST_SLIDERS.map(({ key, label, min, max, step }) => (
                      <div key={key} className="med-slider-row">
                        <div className="med-slider-label">
                          <span>{label}</span>
                          <span className="med-slider-val">
                            {editingImage.adjust[key]}
                            {["brightness", "contrast", "saturation"].includes(key) ? "%" : ""}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={step}
                          value={editingImage.adjust[key]}
                          className="med-slider"
                          onChange={(e) =>
                            updateImage(editingImage.id, {
                              adjust: { ...editingImage.adjust, [key]: Number(e.target.value) },
                            })
                          }
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="med-reset-btn"
                      onClick={() => updateImage(editingImage.id, { adjust: { ...DEFAULT_ADJUST } })}
                    >
                      ↺ Reset
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── VIDEO ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pro-video">
              🎥 Video <span className="pro-optional">(one, up to {VIDEO_LIMIT_TEXT})</span>
            </label>

            <div className="pro-file-row">
              <input
                ref={videoInputRef}
                id="pro-video"
                type="file"
                accept="video/*"
                className="pro-file-input"
                onChange={onVideoChange}
                disabled={loading}
              />
              <label htmlFor="pro-video" className="pro-file-label">
                {video ? `✅ ${video.file.name}` : "Choose video…"}
              </label>

              {video && (
                <button
                  type="button"
                  className="pro-btn pro-btn-ghost"
                  onClick={removeVideo}
                  disabled={loading}
                >
                  Remove
                </button>
              )}
            </div>

            {errors.video && <p className="pro-error">{errors.video}</p>}

            {video && (
              <div className="pro-video-preview">
                <video src={video.url} controls />
                <p className="pro-counter">
                  Duration {formatDuration(video.duration)} /{" "}
                  {formatDuration(PRO_LIMITS.VIDEO_SECONDS_MAX)}
                </p>
              </div>
            )}
          </div>

          {/* ── POST SETTINGS (same three the free composer offers) ── */}
          <div className="pro-field">
            <label className="pro-label">⚙ Post Settings</label>
            <div className="pro-toggle-row">
              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={hideComments}
                  onChange={(e) => setHideComments(e.target.checked)}
                  disabled={loading}
                />
                <span>Hide comments</span>
              </label>

              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={!allowShare}
                  onChange={(e) => setAllowShare(!e.target.checked)}
                  disabled={loading}
                />
                <span>Turn off sharing</span>
              </label>

              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={!allowSave}
                  onChange={(e) => setAllowSave(!e.target.checked)}
                  disabled={loading}
                />
                <span>Turn off saving</span>
              </label>
            </div>
          </div>

          {loading && (
            <div className="pro-progress">
              <div className="pro-progress-bar">
                <div className="pro-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="pro-counter">{progress}% uploaded</span>
            </div>
          )}

          <div className="pro-form-actions">
            <button
              type="button"
              className="pro-btn pro-btn-ghost"
              onClick={() => navigate("/pro-home")}
              disabled={loading}
            >
              ← Pro Home
            </button>

            <button type="submit" className="pro-btn pro-btn-primary" disabled={loading}>
              {loading ? `Publishing ${progress}%` : "🚀 Create Blog/Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProCreateContent;
